import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Shared hook for search-dropdown input components.
 *
 * Encapsulates:
 * - query / isOpen state
 * - outside-click dismissal
 * - a `clearAndClose` helper for selection callbacks
 * - `showDropdown` derived boolean
 */
export function useSearchDropdown() {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const showDropdown = useMemo(() => isOpen && query.trim().length > 0, [isOpen, query]);

  const clearAndClose = useCallback(() => {
    setQuery('');
    setIsOpen(false);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const container = containerRef.current;
      if (!container) return;
      // Use composedPath() instead of contains(e.target) so that a
      // synchronously-unmounted target (e.g. an in-dropdown affordance
      // that hides itself on click — the "Clear" link, the "Add N
      // sources" footer button) doesn't get mis-treated as an "outside"
      // click. React 18 commits discrete events (mousedown) synchronously
      // before the document-level listener runs, so by the time
      // contains() is evaluated the target is already detached from the
      // DOM and contains() returns false. composedPath() is captured at
      // event dispatch and is invariant to subsequent DOM mutations.
      if (!e.composedPath().includes(container)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return {
    query,
    setQuery,
    isOpen,
    setIsOpen,
    containerRef,
    inputRef,
    showDropdown,
    clearAndClose,
  } as const;
}
